import { useMemo } from "react";

export const ElevationProfile = ({ profile = [], gainM = 0, distanceKm = 0, height = 180 }) => {
    const { path, area, minE, maxE, w, h } = useMemo(() => {
        if (!profile || profile.length < 2) return { path: "", area: "", minE: 0, maxE: 0, w: 800, h: height };
        const w = 800;
        const h = height;
        const pad = { top: 20, right: 20, bottom: 30, left: 40 };
        const cw = w - pad.left - pad.right;
        const ch = h - pad.top - pad.bottom;
        const maxD = profile[profile.length - 1][0];
        const eles = profile.map((p) => p[1]);
        const minE = Math.floor(Math.min(...eles) / 50) * 50;
        const maxE = Math.ceil(Math.max(...eles) / 50) * 50;
        const rangeE = Math.max(1, maxE - minE);
        const x = (d) => pad.left + (d / maxD) * cw;
        const y = (e) => pad.top + ch - ((e - minE) / rangeE) * ch;
        const pts = profile.map((p) => `${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ");
        const path = "M " + pts.replace(/ /g, " L ");
        const area = `M ${x(0)},${pad.top + ch} L ${pts.replace(/ /g, " L ")} L ${x(maxD)},${pad.top + ch} Z`;
        return { path, area, minE, maxE, w, h };
    }, [profile, height]);

    if (!profile || profile.length < 2) return null;

    return (
        <div data-testid="elevation-profile" className="w-full">
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="eyebrow mb-2">Elevation</div>
                    <h3 className="font-display font-black uppercase text-[var(--fg)] text-2xl md:text-3xl leading-none tracking-tight">
                        The vertical picture.
                    </h3>
                </div>
                <div className="flex gap-6">
                    <div>
                        <div className="font-display font-black text-2xl md:text-3xl text-[#E87722] leading-none">{gainM.toLocaleString()}<span className="text-sm text-[var(--fg-muted)] ml-1">m</span></div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--fg-muted)] mt-1">Total climb</div>
                    </div>
                    <div>
                        <div className="font-display font-black text-2xl md:text-3xl text-[var(--fg)] leading-none">{maxE}<span className="text-sm text-[var(--fg-muted)] ml-1">m</span></div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--fg-muted)] mt-1">Highest point</div>
                    </div>
                </div>
            </div>
            <div className="border border-[var(--border-hair)] bg-[var(--card-bg)] p-4">
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
                    <defs>
                        <linearGradient id="elev-fill" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor="#E87722" stopOpacity="0.55" />
                            <stop offset="100%" stopColor="#E87722" stopOpacity="0.05" />
                        </linearGradient>
                    </defs>
                    {/* Y-axis labels */}
                    <text x="8" y="26" fontSize="10" fill="var(--fg-muted)" fontFamily="Manrope">{maxE}m</text>
                    <text x="8" y={h - 32} fontSize="10" fill="var(--fg-muted)" fontFamily="Manrope">{minE}m</text>
                    {/* X-axis labels */}
                    <text x="40" y={h - 8} fontSize="10" fill="var(--fg-muted)" fontFamily="Manrope">0 km</text>
                    <text x={w - 40} y={h - 8} fontSize="10" fill="var(--fg-muted)" fontFamily="Manrope" textAnchor="end">{Math.round(distanceKm)} km</text>
                    {/* Grid lines */}
                    <line x1="40" y1={h - 30} x2={w - 20} y2={h - 30} stroke="var(--border-hair)" strokeWidth="1" />
                    <line x1="40" y1="20" x2={w - 20} y2="20" stroke="var(--border-hairline)" strokeWidth="1" />
                    {/* Area */}
                    <path d={area} fill="url(#elev-fill)" />
                    {/* Line */}
                    <path d={path} fill="none" stroke="#E87722" strokeWidth="2" strokeLinejoin="round" />
                </svg>
            </div>
        </div>
    );
};
